import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { cn } from '@/lib/utils'
import { usePrefersReducedMotion } from '@/hooks/use-reduced-motion'
import { enter } from '@/lib/motion'
import { ParallaxImage } from '@/components/motion/parallax-image'

interface ImageSectionProps {
    id: string
    src: string
    alt: string
    className?: string
    priority?: boolean
    appear?: boolean
}

export const ImageSection: FC<ImageSectionProps> = ({
    id,
    src,
    alt,
    className,
    priority = false,
    appear = false
}): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const reducedMotion = usePrefersReducedMotion()

    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ['start end', 'center center']
    })

    const topFiller = useTransform(scrollYProgress, [0, 1], [-16, 0])
    const bottomFiller = useTransform(scrollYProgress, [0, 1], [16, 0])

    return (
        <motion.section
            ref={sectionRef}
            id={id}
            initial={appear ? { opacity: 0, y: 16 } : false}
            animate={appear ? { opacity: 1, y: 0 } : undefined}
            transition={enter(0.3)}
            className={cn('relative z-[2] w-full overflow-clip bg-background', className)}
        >
            <ParallaxImage
                src={src}
                alt={alt}
                priority={priority}
                className='absolute inset-0'
            />

            <motion.div
                aria-hidden='true'
                style={reducedMotion ? undefined : { y: topFiller }}
                className='absolute right-0 top-0 z-[1] h-9 w-1/2 bg-background'
            />

            <motion.div
                aria-hidden='true'
                style={reducedMotion ? undefined : { y: bottomFiller }}
                className='absolute bottom-0 left-0 z-[1] h-9 w-1/2 bg-background'
            />
        </motion.section>
    )
}
